import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ModalBaseComponent } from 'app/shared/components/base/modal-base/modal-base.component';
import { PaymentAction } from 'app/shared/models/payment-action.model';
import { PaymentActionService } from 'app/shared/services/payment-action.service';
import { Constants } from 'app/shared/constants/constants';
import { Utils } from 'app/shared/utils/utils';
import { ToastrService } from "ngx-toastr";

@Component({
  selector: 'app-payment-action',
  templateUrl: './payment-action.component.html',
  styleUrls: ['./payment-action.component.scss']
})
export class PaymentActionComponent extends ModalBaseComponent implements OnInit {

  descriptionLength = 0;
  remittenceLength = 0;
  predefinedAmounts = [{amount: null, cents: null}];
  isEditMode = false;
  isViewMode = false;

  /**
   * The slug the backend refused as already used by another payment action of this company, or null.
   *
   * A duplicate is rejected rather than de-duplicated, because the slug is the public payment URL and an
   * auto-numbered donate-now-2 is an address nobody chose. That makes it the user's decision, so the modal has
   * to stay open and say which value has to change - the toast alone says nothing about where to look.
   */
  rejectedSlug: string = null;

  /**
   * The title as it stood when the backend refused a derived slug, or null.
   *
   * Needed because a derived slug is the server's to compute - see onSubmit - so the client cannot know what a
   * new title would derive to and cannot compare its way out of the message. The title changing is the signal.
   */
  private rejectedForTitle: string = null;

  /**
   * The slug this form is heading for, as far as the client can tell: what the user typed, or the title-derived
   * suggestion shown in the placeholder.
   *
   * Only for display and as a fallback - it is deliberately not what gets submitted. Nothing writes it back
   * into the bound field either: an empty Slug is what marks the field as untouched, so filling it in would
   * silently take the title out of the loop.
   */
  get effectiveSlug(): string {
    return Utils.isStringNullOrWhiteSpace(this.currentObject.Slug)
      ? (this.currentObject.SlugSuggestion || '')
      : this.currentObject.Slug;
  }

  /**
   * Whether the slug this form would submit is the one that was refused.
   *
   * Split by who owns the value. A typed slug is compared directly - the field's pattern restricts it to
   * [a-z0-9-], where the client and the server derive identically - so editing it clears the message and
   * returning to it brings the message back without another round trip. A derived slug cannot be compared,
   * because only the server knows what a title derives to, so the message stands until the title changes.
   */
  get slugIsNotUnique(): boolean {
    if (this.rejectedSlug === null) {
      return false;
    }

    return Utils.isStringNullOrWhiteSpace(this.currentObject.Slug)
      ? this.rejectedForTitle === this.currentObject.Title
      : this.currentObject.Slug === this.rejectedSlug;
  }

  constructor(
    public paymentActionService: PaymentActionService,
    protected activeModal: NgbActiveModal,
    protected modalService: NgbModal,
    protected translateService: TranslateService,
    protected toastrService: ToastrService
  ) {
    super(activeModal);
  }

  trackByIndex(index: number): any {
    return index;
  }

  ngOnInit(): void {
    this.initializeObject();
  }

  initializeObject(): void {
    if (this.currentObject == null) {
      this.currentObject = new PaymentAction();
    }

    super.initializeObject();

    this.translateService.get([
      'paymentAction.addedSuccessfullyMessage',
      'paymentAction.updatedSuccessfullyMessage'
    ]).subscribe(translations => {
      this.addedSuccessfullyMessage = translations['paymentAction.addedSuccessfullyMessage'];
      this.updatedSuccessfullyMessage = translations['paymentAction.updatedSuccessfullyMessage'];
    });

    if ((this.isEditMode || this.isViewMode) && this.currentObject.PredefinedAmountsArray?.length) {
      this.predefinedAmounts = this.currentObject.PredefinedAmountsArray.map(value => {
        const [amount, cents] = value.toString().split('.');
        return {
          amount: parseInt(amount, 10),
          cents: parseInt(cents, 10)
        };
      });
    }
  }

  descriptionChanged(): void {
    this.descriptionLength = this.currentObject.Description?.length || 0;
  }

  remittenceChanged(): void {
    this.remittenceLength = this.currentObject.RemittanceInformation?.length || 0;
  }

  addPredefinedAmount(): void {
    if (this.predefinedAmounts.length < 3) {
      this.predefinedAmounts.push({amount: null, cents: null});
    }
  }

  removePredefinedAmount(index: number): void {
    if (this.predefinedAmounts.length > 1) {
      this.predefinedAmounts.splice(index, 1);
    }
  }

  validateNumericInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
  }

  isValidAmount(amountObj: { amount: number | null; cents: number | null }): boolean {
    const euros = amountObj.amount || 0;
    const cents = amountObj.cents || 0;
    const totalValue = euros + cents / 100;
    return totalValue >= 0.01; // At least 1 cent
  }

  copyPaymentUrl(paymentAction: any): void {
    this.paymentActionService.copyPaymentActionUrl(paymentAction.CompanySlug, paymentAction.Slug, paymentAction.CompanyName);
  }

  onSubmit(form: NgForm): void {
    if (this.predefinedAmounts.some(amount => !this.isValidAmount(amount))) {
      this.translateService.get([
        'paymentAction.eachPredefinedMinimumValueError',
      ]).subscribe(translations => {
        const eachPredefinedMinimumValueError: string = translations['paymentAction.eachPredefinedMinimumValueError'];
        this.toastrService.error(eachPredefinedMinimumValueError);
      });
      return;
    }

    super.onSubmit(form);

    this.currentObject.PredefinedAmounts = JSON.stringify(
      this.predefinedAmounts.map(amount => {
        const euros = amount.amount || 0;
        const cents = amount.cents || 0;
        return `${euros}.${cents}`;
      })
    );

    if (this.isEditMode) {
      this.paymentActionService.putPaymentAction(
        this.currentObject.Id,
        this.currentObject,
        () => this.handleSubmit(form),
        this.updatedSuccessfullyMessage,
        (error: HttpErrorResponse) => this.handleSaveError(error));
    } else {
      // Submitted as-is, with Slug left empty unless the user typed one, so the server derives it.
      //
      // The client deliberately does not send its own suggestion. Utils.generateSlug and the server's
      // GenerateSlug transliterate differently - the server best-fits through a codepage, the client strips
      // undecomposable letters - and they disagree on 80 code points across Latin-1 and Latin Extended-A, so
      // "Ø Fjord" submits as fjord from here but derives as o-fjord there. Sending nothing makes the server the
      // only author of a derived slug; a typed one is safe to send because the field's pattern restricts it to
      // the range where both agree. The refused value comes back on the error for the field to point at.
      this.paymentActionService.createPaymentAction(
        this.currentObject,
        () => this.handleSubmit(form),
        this.addedSuccessfullyMessage,
        (error: HttpErrorResponse) => this.handleSaveError(error));
    }
  }

  /**
   * Keeps a rejected save recoverable: releases the spinner so the modal is usable again, and records a refused
   * slug so the field can point at itself.
   *
   * Recognised by error code rather than message text, which is a sentence meant for a person and would break
   * this the moment it is reworded or translated. Any other failure only releases the spinner - it has already
   * been reported in a toast by the service.
   */
  handleSaveError(error: HttpErrorResponse): void {
    this.handleSubmitFailed();

    if (error && error.error && error.error.ErrorCode === Constants.PaymentActionSlugNotUniqueErrorCode) {
      // Slug on the body is the value the server actually checked. Falling back to the client's own guess only
      // covers an older API that does not send it.
      this.rejectedSlug = error.error.Slug !== undefined && error.error.Slug !== null
        ? error.error.Slug
        : this.effectiveSlug;

      this.rejectedForTitle = this.currentObject.Title;
    }
  }

  /**
   * Keeps the offered slug in step with the title, until the user takes the field over. Once they have typed a
   * slug it is theirs, so retitling must not quietly move the URL out from under them.
   */
  onTitleChange(): void {
    if (!this.isEditMode && Utils.isStringNullOrWhiteSpace(this.currentObject.Slug)) {
      this.currentObject.SlugSuggestion = Utils.generateSlug(this.currentObject.Title, Constants.PaymentActionMaxSlugLength);
    }
  }

  /**
   * Clearing the slug field leaves it empty rather than filling in the suggestion, because empty is what means
   * "follow the title": it hands the field back, so retitling starts moving the slug again. The suggestion is
   * still shown as the placeholder, but it is not submitted - an empty field is sent empty and the server
   * derives the slug itself, for the reason onSubmit gives.
   */
  onSlugChange(newValue: string): void {
    this.currentObject.Slug = newValue || '';
  }

  resetForm(form: NgForm): void {
    this.predefinedAmounts = [{amount: null, cents: null}];
    form.resetForm();
    this.initializeObject();
  }
}
